import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from '@hooks';
import { apiClient } from '@config/api';
import { NamelessCardBulkDetailResult } from '../detail/constants';

interface RetryNamelessCardParams {
  customerId: number;
  namelessRegistrationId: string;
  row: NamelessCardBulkDetailResult;
}

/**
 * Retries a failed nameless card registration.
 */
const useRetryNamelessCard = ({ onSuccess }: { onSuccess?: () => void } = {}) => {
  const [retryingId, setRetryingId] = useState<string | null>(null);
  const { snackbarProps, showSnackbar } = useSnackbar();
  const { t } = useTranslation();

  /**
   * Sends the retry request for a failed registration row.
   */
  const retry = async ({ customerId, namelessRegistrationId, row }: RetryNamelessCardParams) => {
    if (retryingId) return;

    setRetryingId(row.id);
    try {
      await apiClient.patch('/v4/nameless-cards/retry', {
        customerId: String(customerId),
        namelessRegistrationId,
        namelessRegistrationResultIds: [row.id],
        amount: String(row.amount),
      });
      showSnackbar(t('GIFT_CARDS:RETRY_SUCCESS'), 'success');
      onSuccess?.();
    } catch {
      showSnackbar(t('COMMON:ERRORS:DEFAULT_ERROR'), 'error');
    } finally {
      setRetryingId(null);
    }
  };

  return {
    retry,
    retryingId,
    isLoading: Boolean(retryingId),
    snackbarProps,
  };
};

export default useRetryNamelessCard;
